import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  fetchWorkerClearanceHistory,
  fetchWorkerClearances,
  updateMembershipPosition,
  type MembershipRow,
} from '../api';
import { clearanceDocumentLabel } from '@railroute/contracts/core';
import { useResource } from './useResource';
import { admissionLabel, localToday, relativeTerm, rowTone, subdivisionCell } from './personnel-rows';
import { ClearanceAnnulForm } from './ClearanceAnnulForm';
import { ClearanceBasisForm } from './ClearanceBasisForm';
import { ClearanceEntryForm, type ClearanceEntryStart } from './ClearanceEntryForm';
import { historyLines, recordLines, type RecordLine } from './clearance-history';
import { basisMissingMark, cardDate, cardSections, recordStatusLabel, type CardGroup } from './worker-card';

// Карточка работника: всё, что в таблицу не влезает. Таблица отвечает на
// вопрос «у кого проблема», карточка — «что именно и с какого числа».
//
// Две строки здесь — не данные, а честное «этого в системе нет»: стажировка и
// целевой инструктаж. Пустое место на их месте инженер по охране труда прочёл
// бы как «всё в порядке», а это неправда: сведений просто нет.
//
// Issue #524: у того, кому организация доверила вносить документы-основания
// (право `CLEARANCE_EVIDENCE_MANAGE`), здесь же появляются два действия — ввод
// нового допуска и продление имеющегося. У остальных карточка остаётся
// только для чтения: показать кнопку тому, кому сервер откажет, значит соврать.
//
// Issue #525: к ним добавились исправление и аннулирование записи, а карточка
// стала читать ДВА ответа сервера вместо одного:
//
//   `GET /clearances/worker/:n`         — действующие записи и окно дат. Это и
//                                         есть ответ «допущен ли он сейчас»;
//                                         аннулированных записей здесь нет вовсе;
//   `GET /clearances/worker/:n/history` — все записи, ВКЛЮЧАЯ аннулированные,
//                                         и журнал событий по каждой.
//
// Два ответа, а не один, потому что это два разных вопроса. Собрать светофор
// из журнала значило бы посчитать в браузере, даёт ли аннулированная запись
// допуск, — то есть завести второй ответ на вопрос, который решает наряд.
//
// ЖУРНАЛ ВИДЕН ВСЕМ, КТО ЧИТАЕТ ДОПУСКИ. Права он требует того же, что и сами
// записи; исправление и аннулирование — отдельного. Поэтому без права
// «ответственного за допуски» история на месте, а кнопок нет.

export function WorkerCard({
  row,
  token,
  organizationId,
  canManageClearances,
  canEditPosition = false,
  onClose,
  onChanged,
  onUnauthorized,
}: {
  row: MembershipRow;
  token: string;
  organizationId: string;
  /** Вправе ли вошедший вносить, исправлять и аннулировать допуски (`CLEARANCE_EVIDENCE_MANAGE`). */
  canManageClearances: boolean;
  /** Вправе ли вошедший менять должность (`PERSONNEL_MANAGE`, issue #531/#533). */
  canEditPosition?: boolean;
  onClose: () => void;
  /** Запись изменилась: список персонала перечитывает состояние у сервера. */
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  // Последний обработчик закрытия — через ссылку: раздел передаёт его
  // стрелкой, новой на каждую перерисовку, и подписка на неё перевешивала бы
  // слушателя клавиш и переводила фокус на «Закрыть» при каждом вводе в поиск.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Фокус — на открытии карточки и на смене работника, не чаще.
  useEffect(() => {
    closeRef.current?.focus();
  }, [row.id]);

  const tone = rowTone(row);

  // Карточка стоит СБОКУ от таблицы, а не поверх неё: раздел сжимает таблицу
  // на ширину карточки (`.panel:has(.worker-card)` в app.css). Таблица видна
  // целиком, и по соседней строке можно открыть следующего работника, не
  // закрывая эту карточку. Поэтому это не модальное окно и затемнения нет.
  return (
    <aside className="worker-card" role="dialog" aria-modal="false" aria-labelledby="worker-card-title">
      <header className="worker-card__head">
        <div>
          <h2 id="worker-card-title" className="worker-card__name">{row.user.name}</h2>
          <p className="worker-card__meta">
            таб. № {row.employeeNumber ?? '—'} · {subdivisionCell(row)} ·{' '}
            {row.isActive ? 'работает' : 'не работает'}
          </p>
        </div>
        <button ref={closeRef} className="ui-btn ui-btn--ghost" onClick={onClose} aria-label="Закрыть карточку">
          Закрыть
        </button>
      </header>

      <PersonDetails
        key={row.id}
        row={row}
        token={token}
        organizationId={organizationId}
        canEditPosition={canEditPosition}
        onChanged={onChanged}
        onUnauthorized={onUnauthorized}
      />

      <p className={`status-pill status-pill--${tone}`}>
        {row.isActive ? admissionLabel(row) : 'Не работает'}
      </p>

      {row.employeeNumber === null ? (
        <p className="section__note">
          У работника не заведён табельный номер в организации, а записи допусков ищутся по нему.
          Карточку откроет администратор организации, когда номер будет внесён.
        </p>
      ) : (
        <CardBody
          // Карточка не закрывается при переходе на соседнюю строку таблицы:
          // сменился работник — содержимое начинается заново. Без этого
          // начатый черновик допуска остался бы на экране и ушёл бы уже
          // ДРУГОМУ работнику.
          key={row.employeeNumber}
          employeeNumber={row.employeeNumber}
          workerName={row.user.name}
          token={token}
          organizationId={organizationId}
          canManageClearances={canManageClearances}
          onChanged={onChanged}
          onUnauthorized={onUnauthorized}
        />
      )}
    </aside>
  );
}

/**
 * Год рождения и должность — графы 3, 4 и 7 журнала инструктажа (issue #531).
 *
 * Год рождения только показывается: он вычислен сервером из ИИН при вводе ИИН,
 * и поля для ручного ввода нет — вручную введённый год разошёлся бы с ИИН.
 * Нет года (ИИН не внесён или по нему год не определить) — так и сказано.
 *
 * Должность правится тем, у кого есть право менять сотрудника; пустое поле
 * снимает должность.
 */
function PersonDetails({
  row,
  token,
  organizationId,
  canEditPosition,
  onChanged,
  onUnauthorized,
}: {
  row: MembershipRow;
  token: string;
  organizationId: string;
  canEditPosition: boolean;
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.position ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateMembershipPosition(token, organizationId, row.id, draft);
      setEditing(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Должность не сохранилась');
    } finally {
      setSaving(false);
    }
  };

  return (
    <dl className="worker-card__details">
      <dt>Год рождения</dt>
      <dd>{row.user.birthYear ?? 'не определён — по ИИН, если он внесён'}</dd>
      <dt>Должность</dt>
      <dd>
        {editing ? (
          <>
            <input
              aria-label="Должность"
              value={draft}
              maxLength={255}
              onChange={(event) => setDraft(event.target.value)}
              disabled={saving}
            />
            <button type="button" className="ui-btn" onClick={() => void save()} disabled={saving}>
              Сохранить
            </button>
            <button
              type="button"
              className="ui-btn ui-btn--ghost"
              onClick={() => {
                setEditing(false);
                setDraft(row.position ?? '');
              }}
              disabled={saving}
            >
              Отмена
            </button>
          </>
        ) : (
          <>
            {row.position ?? '—'}
            {canEditPosition ? (
              <button type="button" className="ui-btn ui-btn--ghost" onClick={() => setEditing(true)}>
                Изменить
              </button>
            ) : null}
          </>
        )}
        {error ? <p className="ui-error">{error}</p> : null}
      </dd>
    </dl>
  );
}

/**
 * Отказ сервера на чтение допусков — словами, а не кодом.
 *
 * Общий для обеих ручек: держатся они на одном праве и на одной старой роли
 * входа, и два разных объяснения одного отказа сбивали бы с толку.
 */
function clearanceReadFailure(error: unknown): Error {
  // Отказ по правам — не поломка, и сказать это надо прямо. Ручки пока
  // держатся и на старой роли входа (SENIOR или ADMIN), а не только на праве
  // читать допуски; сервер отвечает на это общим «Forbidden», из которого
  // человек ничего не поймёт. Распознаётся по коду ответа, а не по тексту:
  // текст сервера может смениться, код — нет.
  if (error instanceof ApiError && error.status === 403) {
    return new Error(
      'Смотреть все записи допусков работника может старший или администратор — у этой учётной записи такого права нет.',
    );
  }
  // Ответ пришёл, но не того вида: это ошибка сервера или сборки, а не
  // человека. Сырой разбор схемы на экране — простыня служебного текста, из
  // которой нельзя понять даже, что случилось. Человеку — фраза, что делать;
  // подробности остаются в консоли браузера для разбора.
  if (!(error instanceof ApiError)) {
    console.error('worker card: malformed clearances response', error);
    return new Error('Сервер прислал записи допусков в неожиданном виде. Сообщите администратору.');
  }
  return error;
}

function CardBody({
  employeeNumber,
  workerName,
  token,
  organizationId,
  canManageClearances,
  onChanged,
  onUnauthorized,
}: {
  employeeNumber: string;
  workerName: string;
  token: string;
  organizationId: string;
  canManageClearances: boolean;
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  const [entry, setEntry] = useState<ClearanceEntryStart | null>(null);
  const [annulling, setAnnulling] = useState<RecordLine | null>(null);
  // Issue #537: дозаполнение основания у записи без номера или даты документа.
  const [completing, setCompleting] = useState<{ id: string; typeName: string; validFrom: string } | null>(null);
  const record = useResource(
    () =>
      fetchWorkerClearances(employeeNumber, token, organizationId).catch((error: unknown) => {
        throw clearanceReadFailure(error);
      }),
    onUnauthorized,
    [employeeNumber, token, organizationId],
  );
  const journal = useResource(
    () =>
      fetchWorkerClearanceHistory(employeeNumber, token, organizationId).catch((error: unknown) => {
        throw clearanceReadFailure(error);
      }),
    onUnauthorized,
    [employeeNumber, token, organizationId],
  );

  // Пока идёт перечитывание после записи, прежние записи остаются на экране:
  // «Загружаю записи…» вместо карточки означало бы, что сохранение её закрыло.
  if (record.loading && record.data === null) return <p className="section__note">Загружаю записи…</p>;
  if (record.error) return <p className="ui-error">{record.error}</p>;
  const sections = cardSections(record.data ?? { clearances: [] });
  const today = localToday();

  // Запись внесена, исправлена или аннулирована: и карточка, и строка
  // работника в списке перечитывают состояние У СЕРВЕРА. Пересчитать «допущен
  // ли он теперь» в браузере значило бы завести второй ответ на вопрос,
  // который решает наряд.
  const saved = () => {
    setEntry(null);
    setAnnulling(null);
    setCompleting(null);
    record.reload();
    journal.reload();
    onChanged();
  };

  // Окно дат документа — настройка ОРГАНИЗАЦИИ, и приходит она вместе с
  // записями работника. Своего умолчания панель не заводит: разойдись оно с
  // настройкой, подсказка под полем обещала бы не то, что примет сервер.
  // Нет ответа сервера — нет и ввода: форма без окна дат либо запирала бы
  // лишнее, либо обещала лишнее.
  const maxAgeDays = record.data?.documentDateMaxAgeDays ?? null;
  const mayEnter = canManageClearances && maxAgeDays !== null;

  const renew = mayEnter
    ? (group: CardGroup) => {
        setAnnulling(null);
        setCompleting(null);
        setEntry({
          mode: 'renew',
          clearanceTypeId: group.latest.clearanceTypeId,
          issuedBy: group.latest.issuedBy,
          typeName: group.name,
        });
      }
    : undefined;

  // Исправление и аннулирование — действия над КОНКРЕТНОЙ записью, поэтому
  // стоят они в списке всех записей, а не у вида допуска: в нём есть и те
  // записи, что уже не последние, и опечатку правят ровно в той бумаге, где
  // она стоит. Продление, наоборот, относится к виду: это новый документ.
  const correct = mayEnter
    ? (line: RecordLine) => {
        setAnnulling(null);
        setCompleting(null);
        setEntry({
          mode: 'correct',
          clearanceId: line.id,
          typeName: line.typeName,
          record: {
            clearanceTypeId: line.record.clearanceTypeId,
            category: line.category,
            documentNumber: line.record.documentNumber,
            // Issue #537: правится дата ДОКУМЕНТА. Исправление предлагается
            // только записи с основанием, так что дата у неё есть.
            documentDate: line.record.documentDate ?? line.record.issuedAt,
            expiresAt: line.record.expiresAt,
            issuedBy: line.record.issuedBy,
            electricalSafetyGroup: line.record.electricalSafetyGroup,
            notes: line.record.notes,
          },
        });
      }
    : undefined;

  const annul = mayEnter
    ? (line: RecordLine) => {
        setEntry(null);
        setCompleting(null);
        setAnnulling(line);
      }
    : undefined;

  // Issue #537: дозаполнить основание можно только у действующей записи —
  // той, что помечена в разделе своего вида. Кнопка — у ответственного за
  // допуски; пометку видят все, кто открыл карточку.
  const markedGroups = [...sections.briefings, ...sections.medical, ...sections.electrical, ...sections.other].filter(
    basisMissingMark,
  );
  const completableIds = new Set(markedGroups.map((group) => group.latest.id));
  const completeBasis = mayEnter
    ? (target: { id: string; typeName: string; validFrom: string }) => {
        setEntry(null);
        setAnnulling(null);
        setCompleting(target);
      }
    : undefined;
  const completeGroup = completeBasis
    ? (group: CardGroup) => completeBasis({ id: group.latest.id, typeName: group.name, validFrom: group.latest.issuedAt })
    : undefined;
  const completeLine = completeBasis
    ? (line: RecordLine) =>
        completeBasis({ id: line.id, typeName: line.typeName, validFrom: line.record.issuedAt })
    : undefined;

  const records = journal.data ? recordLines(journal.data) : [];
  const events = journal.data ? historyLines(journal.data) : [];

  return (
    <>
      {mayEnter && maxAgeDays !== null ? (
        entry !== null ? (
          <ClearanceEntryForm
            // Нажали «Продлить» или «Исправить» у другой записи, не закрыв
            // форму, — форма начинается заново: черновик прежней записи ушёл
            // бы уже в ДРУГУЮ.
            key={
              entry.mode === 'renew'
                ? `renew:${entry.clearanceTypeId}`
                : entry.mode === 'correct'
                  ? `correct:${entry.clearanceId}`
                  : 'new'
            }
            start={entry}
            employeeNumber={employeeNumber}
            workerName={workerName}
            maxAgeDays={maxAgeDays}
            token={token}
            organizationId={organizationId}
            onSaved={saved}
            onCancel={() => setEntry(null)}
            onUnauthorized={onUnauthorized}
          />
        ) : completing !== null ? (
          <ClearanceBasisForm
            key={`basis:${completing.id}`}
            clearanceId={completing.id}
            typeName={completing.typeName}
            validFrom={completing.validFrom}
            token={token}
            organizationId={organizationId}
            onDone={saved}
            onCancel={() => setCompleting(null)}
            onUnauthorized={onUnauthorized}
          />
        ) : annulling !== null ? (
          <ClearanceAnnulForm
            key={`annul:${annulling.id}`}
            clearanceId={annulling.id}
            typeName={annulling.typeName}
            documentNumber={annulling.documentNumber}
            issuedAt={annulling.documentDate ?? annulling.issuedAt}
            token={token}
            organizationId={organizationId}
            onDone={saved}
            onCancel={() => setAnnulling(null)}
            onUnauthorized={onUnauthorized}
          />
        ) : (
          <button type="button" className="ui-btn ui-btn--primary clearance-entry__open" onClick={() => setEntry({ mode: 'new' })}>
            Добавить допуск
          </button>
        )
      ) : null}

      <CardSection
        title="Инструктажи"
        groups={sections.briefings}
        today={today}
        empty="Записей об инструктажах нет."
        onRenew={renew}
        onCompleteBasis={completeGroup}
      />
      <dl className="worker-card__facts">
        <dt>Целевой инструктаж</dt>
        <dd>Проводится по каждому наряду отдельно и хранится в наряде. Смотрите карточку наряда.</dd>
        <dt>Стажировка</dt>
        <dd>В системе пока не ведётся.</dd>
      </dl>
      <CardSection
        title="Медосмотр"
        groups={sections.medical}
        today={today}
        empty="Записей о медосмотре нет."
        onRenew={renew}
        onCompleteBasis={completeGroup}
      />
      <CardSection
        title="Электробезопасность"
        groups={sections.electrical}
        today={today}
        empty="Записей о группе по электробезопасности нет."
        onRenew={renew}
        onCompleteBasis={completeGroup}
      />
      {sections.other.length > 0 ? (
        <CardSection
          title="Прочие допуски"
          groups={sections.other}
          today={today}
          empty=""
          onRenew={renew}
          onCompleteBasis={completeGroup}
        />
      ) : null}

      {journal.error !== null ? (
        <p className="ui-error">{journal.error}</p>
      ) : (
        <>
          <RecordList
            lines={records}
            loading={journal.loading}
            onCorrect={correct}
            onAnnul={annul}
            completableIds={completableIds}
            onCompleteBasis={completeLine}
          />
          <JournalList events={events} loading={journal.loading} />
        </>
      )}
    </>
  );
}

/**
 * Все записи работника, новые сверху, ВКЛЮЧАЯ аннулированные (issue #525).
 *
 * Аннулированная стоит здесь зачёркнутой, с пометкой «аннулирован» и
 * причиной, и рядом сказано прямо, что в допуск она не идёт. Убрать её с
 * экрана значило бы оставить вопрос «а был ли такой документ» без ответа;
 * оставить без пометки — дать зачесть её как действующую.
 */
function RecordList({
  lines,
  loading,
  onCorrect,
  onAnnul,
  completableIds,
  onCompleteBasis,
}: {
  lines: RecordLine[];
  loading: boolean;
  /** `undefined` — прав на исправление нет, и кнопки нет. */
  onCorrect?: (line: RecordLine) => void;
  /** `undefined` — прав на аннулирование нет, и кнопки нет. */
  onAnnul?: (line: RecordLine) => void;
  /** Issue #537: действующие записи без основания — их можно дозаполнить. */
  completableIds: ReadonlySet<string>;
  /** `undefined` — прав на дозаполнение нет, и кнопки нет. */
  onCompleteBasis?: (line: RecordLine) => void;
}) {
  return (
    <section className="worker-card__section">
      <h3 className="worker-card__title">Все записи</h3>
      {lines.length === 0 ? (
        <p className="section__note">{loading ? 'Загружаю записи…' : 'Записей нет.'}</p>
      ) : (
        <ul className="worker-card__records">
          {lines.map((line) => (
            <li key={line.id} className={`record-item${line.annulled ? ' record-item--annulled' : ''}`}>
              <div className="record-item__head">
                <span className="record-item__name">
                  {line.typeName}
                  {line.group ? `, группа ${line.group}` : ''}
                </span>
                <span className="record-item__status">{line.status}</span>
              </div>
              <div className="record-item__detail">
                {[
                  line.documentNumber ? clearanceDocumentLabel(line.documentNumber) : null,
                  line.documentDate ? `от ${line.documentDate}` : null,
                  // Issue #537: начало действия отдельно — только когда оно не
                  // совпадает с датой документа (записи до #537).
                  line.documentDate !== line.issuedAt ? `действует с ${line.issuedAt}` : null,
                  line.expiresAt ? `действует до ${line.expiresAt}` : 'бессрочно',
                  // Issue #543: инструктаж — по журналу или внесён вручную.
                  line.sourceLabel,
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
              </div>
              {line.annulled ? (
                <p className="record-item__annulment">
                  Аннулирован {line.annulledAt ?? ''}. Причина: {line.annulmentReason ?? 'не записана'}. В допуск
                  работника эта запись не идёт.
                </p>
              ) : null}
              {!line.annulled && line.basisMissing && completableIds.has(line.id) ? (
                <span className="basis-missing">Нет документа-основания</span>
              ) : null}
              {/* Кнопок «Удалить» нет нигде: ошибочную запись аннулируют. */}
              {!line.annulled && (onCorrect || onAnnul) ? (
                <div className="record-item__actions">
                  {/* Issue #537: запись без основания не исправляют, а
                      дозаполняют — и только действующую. */}
                  {line.basisMissing && onCompleteBasis && completableIds.has(line.id) ? (
                    <button
                      type="button"
                      className="ui-btn ui-btn--ghost record-item__action"
                      onClick={() => onCompleteBasis(line)}
                    >
                      Дозаполнить основание
                    </button>
                  ) : null}
                  {onCorrect && !line.basisMissing ? (
                    <button type="button" className="ui-btn ui-btn--ghost record-item__action" onClick={() => onCorrect(line)}>
                      Исправить
                    </button>
                  ) : null}
                  {onAnnul ? (
                    <button type="button" className="ui-btn ui-btn--ghost record-item__action" onClick={() => onAnnul(line)}>
                      Аннулировать
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * История допусков работника — журнал сервера (issue #525).
 *
 * По строке на событие: что сделали и с каким допуском, кто, дата документа и
 * время ввода раздельно, «было → стало» по каждому изменённому полю и причина
 * у исправления и аннулирования. Ничего из этого панель не выводит сама.
 */
function JournalList({ events, loading }: { events: ReturnType<typeof historyLines>; loading: boolean }) {
  return (
    <section className="worker-card__section">
      <h3 className="worker-card__title">История допусков</h3>
      {events.length === 0 ? (
        <p className="section__note">
          {loading
            ? 'Загружаю историю…'
            : 'Журнал пуст: по этому работнику записей о вводе и изменениях ещё не делали.'}
        </p>
      ) : (
        <ol className="worker-card__history">
          {events.map((event) => (
            <li key={event.id} className="history-item">
              <div className="history-item__head">
                <span className="history-item__name">
                  {event.action}: {event.typeName}
                </span>
                <span className="history-item__date">{event.enteredAt}</span>
              </div>
              {/* Дата документа и время ввода — раздельно и обе: документ
                  выдан одним числом, внесён другим, и путать их нельзя. */}
              <div className="history-item__detail">
                Дата документа: {event.documentDate} · внесено {event.enteredAt}
              </div>
              <div className="history-item__detail history-item__issuer">{event.actor}</div>
              {event.changes.length > 0 ? (
                <ul className="history-item__changes">
                  {event.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              ) : null}
              {event.reason ? (
                <div className="history-item__detail history-item__reason">Причина: {event.reason}</div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function CardSection({
  title,
  groups,
  today,
  empty,
  onRenew,
  onCompleteBasis,
}: {
  title: string;
  groups: CardGroup[];
  today: ReturnType<typeof localToday>;
  empty: string;
  /**
   * Продлить допуск этого вида. `undefined` — прав на это нет, и кнопки нет.
   *
   * Кнопка стоит у ЛЮБОЙ записи, а не только у просроченной: продлевают и
   * заранее, до истечения срока, и это обычный порядок, а не исключение.
   */
  onRenew?: (group: CardGroup) => void;
  /** Issue #537: дозаполнить основание. `undefined` — прав нет, и кнопки нет. */
  onCompleteBasis?: (group: CardGroup) => void;
}) {
  return (
    <section className="worker-card__section">
      <h3 className="worker-card__title">{title}</h3>
      {groups.length === 0 ? (
        <p className="section__note">{empty}</p>
      ) : (
        <ul className="worker-card__list">
          {groups.map((group) => {
            const latest = group.latest;
            const status = latest.computedStatus;
            return (
              <li key={group.typeId} className={`worker-card__item worker-card__item--${status}`}>
                <span className="worker-card__item-name">{group.name}</span>
                <span className="worker-card__item-term">
                  {latest.electricalSafetyGroup ? `группа ${latest.electricalSafetyGroup} · ` : ''}
                  {latest.expiresAt
                    ? relativeTerm({ status, expiresAt: latest.expiresAt.slice(0, 10) }, today)
                    : `проведён ${cardDate(latest.issuedAt)}`}
                  {latest.expiresAt ? (
                    <span className="worker-card__date">
                      {' '}· до {cardDate(latest.expiresAt)} · {recordStatusLabel(status)}
                    </span>
                  ) : null}
                </span>
                {latest.documentNumber ? (
                  <span className="worker-card__date">{clearanceDocumentLabel(latest.documentNumber)}</span>
                ) : null}
                {basisMissingMark(group) ? (
                  <span className="basis-missing">Нет документа-основания</span>
                ) : null}
                {basisMissingMark(group) && onCompleteBasis ? (
                  <button
                    type="button"
                    className="ui-btn ui-btn--ghost worker-card__renew"
                    onClick={() => onCompleteBasis(group)}
                  >
                    Дозаполнить основание
                  </button>
                ) : null}
                {onRenew ? (
                  <button type="button" className="ui-btn ui-btn--ghost worker-card__renew" onClick={() => onRenew(group)}>
                    Продлить
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
